import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { Card, Badge, Section, TH, TD, Empty, Button } from "../components/ui";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

function fmtDate(s) { return s ? new Date(s).toLocaleString("it-IT") : "—"; }

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/dashboard"); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteRun = async (r) => {
    if (!window.confirm(`Cancellare l'esecuzione ${r.entity} · ${r.mode}?`)) return;
    try {
      await api.delete(`/sync-runs/${r.id}`);
      toast.success("Esecuzione cancellata");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <Section
        title="Dashboard Globale"
        subtitle="Stato sincronizzazione società Zoho → FacturaDirecta"
        action={<Button variant="secondary" size="sm" onClick={load} data-testid="btn-refresh-dashboard"><RefreshCw size={14} className="mr-1.5" /> Ricarica</Button>}
      >
        <div className="grid grid-cols-4 gap-4">
          {[
            ["Società", data?.totals?.companies ?? "—"],
            ["Attive", data?.totals?.active ?? "—"],
            ["Coda Revisione", data?.totals?.review_open ?? "—"],
            ["Run totali", data?.totals?.runs_total ?? "—"],
          ].map(([label, val]) => (
            <Card key={label} className="p-5">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-medium">{label}</div>
              <div className="text-3xl font-display font-bold mt-1 tabular-nums">{val}</div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Società" subtitle="Una riga per società con metriche di sync">
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border-default bg-surface-hover/50">
              <tr>
                <TH>Società</TH><TH>Stato</TH><TH>Ultimo sync</TH><TH>Prossimo sync</TH>
                <TH>Clienti OK</TH><TH>Fatture OK</TH><TH>Note credito OK</TH>
                <TH>Errori</TH><TH>Revisione</TH><TH></TH>
              </tr>
            </thead>
            <tbody>
              {(data?.companies || []).map((c) => (
                <tr key={c.company_id} className="border-b border-border-divider table-row-hover" data-testid={`row-company-${c.company_id}`}>
                  <TD className="font-medium">{c.name}</TD>
                  <TD>{c.is_active
                    ? <Badge tone="success">Attiva</Badge>
                    : <Badge tone="default">Disattiva</Badge>}</TD>
                  <TD className="font-mono text-xs">{fmtDate(c.last_sync_at)}</TD>
                  <TD className="font-mono text-xs">{fmtDate(c.next_sync_at)}</TD>
                  <TD className="tabular-nums">{c.contacts_ok}</TD>
                  <TD className="tabular-nums">{c.invoices_ok}</TD>
                  <TD className="tabular-nums">{c.credit_notes_ok}</TD>
                  <TD>{c.errors_open > 0 ? <Badge tone="error">{c.errors_open}</Badge> : <span className="text-ink-subtle">0</span>}</TD>
                  <TD>{c.review_open > 0 ? <Badge tone="warning">{c.review_open}</Badge> : <span className="text-ink-subtle">0</span>}</TD>
                  <TD>
                    <Link to={`/companies/${c.company_id}`} className="text-sm font-medium text-ink underline-offset-2 hover:underline" data-testid={`link-company-${c.company_id}`}>
                      Apri →
                    </Link>
                  </TD>
                </tr>
              ))}
              {(!loading && (data?.companies || []).length === 0) && (
                <tr><td colSpan={10}><Empty>Nessuna società configurata. <Link to="/companies" className="underline">Creane una</Link>.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </Section>

      <Section title="Ultime esecuzioni">
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border-default bg-surface-hover/50">
              <tr><TH>Inizio</TH><TH>Società</TH><TH>Entità</TH><TH>Modalità</TH><TH>Stato</TH><TH>Letti</TH><TH>OK</TH><TH>Errori</TH><TH></TH></tr>
            </thead>
            <tbody>
              {(data?.recent_runs || []).map((r) => (
                <tr key={r.id} className="border-b border-border-divider">
                  <TD className="font-mono text-xs">{fmtDate(r.started_at)}</TD>
                  <TD className="font-mono text-xs">{r.company_id.slice(0,8)}…</TD>
                  <TD>{r.entity}</TD>
                  <TD>{r.mode === "dry_run" ? <Badge tone="dryrun">Dry-Run</Badge> : <Badge tone="info">Reale</Badge>}</TD>
                  <TD><StatusBadge status={r.status} /></TD>
                  <TD className="tabular-nums">{r.records_read}</TD>
                  <TD className="tabular-nums">{r.records_created + r.records_updated}</TD>
                  <TD className="tabular-nums">{r.records_failed + r.records_blocked}</TD>
                  <TD>
                    <button
                      onClick={() => deleteRun(r)}
                      disabled={r.status === "running"}
                      title={r.status === "running" ? "Run in esecuzione" : "Cancella esecuzione"}
                      className="p-1.5 rounded-md text-ink-muted hover:text-error hover:bg-error/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      data-testid={`btn-delete-run-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </TD>
                </tr>
              ))}
              {(data?.recent_runs || []).length === 0 && (
                <tr><td colSpan={9}><Empty>Nessuna esecuzione ancora.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    success: ["success", "Completato"],
    success_with_warnings: ["warning", "Con avvisi"],
    failed: ["error", "Fallito"],
    running: ["info", "In corso"],
    pending: ["default", "In attesa"],
    cancelled: ["default", "Annullato"],
    blocked_requires_review: ["warning", "Da revisione"],
  };
  const [tone, label] = map[status] || ["default", status];
  return <Badge tone={tone}>{label}</Badge>;
}
